import { useEffect, useRef, useState } from 'react'

import { useSearchParams } from 'react-router-dom'

import { getDefaultTokens } from '@src/configs/defaults'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { useExchangeState } from '@src/pages/Exchange/context/ExchangeStateContext'
import { isInEmbedMode } from '@src/utils/isInEmbedMode'
import { parseChains, parseTokenParam } from '@src/utils/urlParams'
import { useTokensStore } from '@src/zustand_stores'
import { useConfigStore } from '@src/zustand_stores/config.store'

/**
 * Hook that manages the default tokens in embed mode.
 * It will select appropriate tokens based on restricted chains from URL parameters.
 *
 * This hook now coordinates with the EmbedPage component to handle the case
 * where specific tokens are requested via URL params but need to be validated
 * against the restricted chains parameter.
 */
export function useEmbedTokenDefaults() {
    const [searchParams] = useSearchParams()
    const tokens = useTokensStore(state => state.tokens)
    const tokensLoaded = tokens.length > 0
    const restrictedChains = useConfigStore(state => state.restrictedChains)
    const updateConfig = useConfigStore(state => state.updateConfig)
    const { updateSellData, updateBuyData, sellData, buyData } = useExchangeDataContext()
    const { setChainId } = useExchangeState()

    // Get URL parameters
    const fromParam = searchParams.get('from')
    const toParam = searchParams.get('to')
    const chainsParam = searchParams.get('chains')
    const hasSpecificTokenParams =
        fromParam ||
        toParam ||
        searchParams.get('sellChain') ||
        searchParams.get('sellToken') ||
        searchParams.get('buyChain') ||
        searchParams.get('buyToken')

    // Track chain param processing
    const [processedChains, setProcessedChains] = useState(false)

    // Track if initial default tokens have been set
    const isInitializedRef = useRef(false)

    // First, handle the chains parameter and update restricted chains in the config store
    useEffect(() => {
        // Only run in embed mode
        if (!isInEmbedMode()) {
            return
        }

        // Parse chains from URL parameter and update config store
        const parsedChains = chainsParam ? parseChains(chainsParam) : null

        if (parsedChains && (!restrictedChains || !arraysEqual(parsedChains, restrictedChains))) {
            console.log('Setting restricted chains:', parsedChains)
            updateConfig({
                restrictedChains: parsedChains,
            })
            setProcessedChains(true)
        }
    }, [chainsParam, updateConfig, restrictedChains])

    // Then handle token initialization once chains have been processed and tokens are loaded
    // BUT only if no specific token params were provided
    useEffect(() => {
        // Only run in embed mode and only when tokens are loaded
        if (!isInEmbedMode() || !tokensLoaded) {
            return
        }

        // If this is not the first initialization, only update when chains change or tokens load
        if (isInitializedRef.current && !processedChains) {
            return
        }

        // If we have specific token parameters, don't initialize with defaults
        // as they'll be handled by the EmbedPage component's token initialization
        if (hasSpecificTokenParams) {
            console.log('Specific token parameters found, skipping default token initialization')
            isInitializedRef.current = true
            return
        }

        // Get default tokens from our updated defaults utility
        const { sellToken, buyToken } = getDefaultTokens()

        console.log('Selected default tokens:', {
            sellToken: `${sellToken.symbol} (${sellToken.chainId})`,
            buyToken: `${buyToken.symbol} (${buyToken.chainId})`,
            restrictedChains,
        })

        // Only update if the tokens are different from current values and values are 0
        if (
            (sellData.token?.address !== sellToken.address || sellData.chainId !== sellToken.chainId) &&
            sellData.value === BigInt(0) &&
            (buyData.token?.address !== buyToken.address || buyData.chainId !== buyToken.chainId) &&
            buyData.value === BigInt(0)
        ) {
            // Update sell data
            updateSellData({
                token: sellToken,
                chainId: sellToken.chainId,
            })

            // Update buy data
            updateBuyData({
                token: buyToken,
                chainId: buyToken.chainId,
            })

            // Set chain ID to sell token's chain
            setChainId(sellToken.chainId)
        }

        // Mark as initialized
        isInitializedRef.current = true
    }, [
        tokensLoaded,
        processedChains,
        hasSpecificTokenParams,
        sellData,
        buyData,
        updateSellData,
        updateBuyData,
        setChainId,
        restrictedChains,
    ])
}

// Helper function to compare arrays
function arraysEqual(a: any[], b: any[]): boolean {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false
    }
    return true
}
